import { TooltipTrigger } from '../Tooltips/TooltipStyles';
import styled, { css } from 'styled-components';
import { BREAKPOINTS, COLORS, focusStyles } from '../styles';
import { ComponentXXSStyling, ComponentTextStyle, ComponentSStyling, ComponentMStyling, ComponentXSStyling } from '../styles/typography';
import {Size, States} from '../types';

export const InputWrapper = styled.div<{ readOnly?: boolean; disabled?: boolean; margin?: string }>`
  outline: none;
  ${(props) => (props.readOnly || props.disabled ? 'cursor: not-allowed;' : '')};
  ${(props) => (props.margin ? `margin: ${props.margin};` : '')}
  position: relative;
  
  ${TooltipTrigger('hover')}

`;

export const readOnlyState = css`
  background-color: ${COLORS.neutral_100};
  box-shadow: inset 0 0 0 1px ${COLORS.neutral_300} !important;
  color: ${COLORS.neutral_600} !important;
  pointer-events: none;
  outline: none;
  cursor: not-allowed;
`;
export const activeErrorMessageState = css`
  box-shadow: inset 0 0 0 2px ${COLORS.critical_400};
`;

const InputFieldStyling = styled.input<{
  activeErrorMessage?: boolean;
  active?: boolean;
  withoutBorder?: boolean;
  size?: string;
  suppressReadOnlyStyles?: boolean;
  extraRightPadding?: number;
}>`
  height: 48px;
  width: 100%;
  border-radius: 4px;
  margin-bottom: 4px;

  border: 0;
  -webkit-appearance: none;
  ${(props) =>
    props.withoutBorder
      ? `-webkit-box-shadow: none;
      -moz-box-shadow: none;
      box-shadow: none;
      `
      : `-webkit-box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_400};
      -moz-box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_400};
      box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_400};`}
  background-color: #fff;
  box-sizing: border-box;
  padding: 0 ${props => 16 + (props.extraRightPadding ?? 0)}px 0 16px !important;
  text-overflow: ellipsis;

  outline: none;
  overflow: hidden;
  position: relative;

  font-size: 16px;

  &::placeholder {
    ${ComponentSStyling(ComponentTextStyle.Italic, COLORS.neutral_500)}
  }

  ${BREAKPOINTS.MEDIUM} {
    font-size: 18px;
    line-height: 18px;
    height: 56px;
    padding: 0 16px !important;

    &::placeholder {
      ${ComponentMStyling(ComponentTextStyle.Italic, COLORS.neutral_500)}
    }
  }

  &.small {
    font-size: 16px;
    height: 48px;
    &::placeholder {
      font-size: 16px;
    }
  }
  &.medium {
    font-size: 18px;
    height: 56px;
    &::placeholder {
      font-size: 18px;
    }
  }

  color: ${COLORS.black} !important;

  ${(props) => (props.activeErrorMessage ? activeErrorMessageState : '')}

  &:read-only:not(:disabled) {
    ${(props) => (props.suppressReadOnlyStyles ? '' : readOnlyState)}
  }

  &:disabled {
    border: 1px solid ${COLORS.neutral_100};
    pointer-events: none;
    box-shadow: none !important;
    outline: none;
    cursor: not-allowed;
    color: ${COLORS.neutral_300} !important;
  }
  &:focus:not(.focus-visible) {
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
  }

  &.focus-visible {
    ${focusStyles}
  }

  &.invalid {
    box-shadow: inset 0 0 0 2px ${COLORS.critical_400};
  }
  &.valid {
    box-shadow: inset 0 0 0 2px ${COLORS.correct_500};
  }

  &:not(.placeholder) {
    line-height: 40px;
  }

  &:hover {
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_700};
  }

  &.active {
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
  }

  &.show-checkmark.valid {
    background-image: none;
  }

  &.show-checkmark.pending {
    background-image: none;
  }
`;

const ValidationStyling = css`
  &.error-msg {
    ${ComponentXXSStyling(ComponentTextStyle.Regular, COLORS.black)}
    text-align: center;
    font-weight: 400;
    box-sizing: border-box;
  }

  &.error-msg {
    padding-top: 8px;
    padding-bottom: 0;
    ${ComponentXXSStyling(ComponentTextStyle.Regular, '#e76468')}
    font-weight: 700;
    line-height: 15px;
  }
`;

export const Warning = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  ${ComponentXXSStyling(ComponentTextStyle.Regular, null)}

  svg {
    width: 16px;
    height: 16px;
  }

  ${BREAKPOINTS.MEDIUM} {
    ${ComponentXSStyling(ComponentTextStyle.Regular, null)}
    svg {
      width: 20px;
      height: 20px;
    }
  }

  &.small {
    ${ComponentXXSStyling(ComponentTextStyle.Regular, null)}
    svg {
      width: 16px;
      height: 16px;
    }
  }
  &.medium {
    ${ComponentXSStyling(ComponentTextStyle.Regular, null)}
    svg {
      width: 20px;
      height: 20px;
    }
  }

  & * {
    vertical-align: middle;
    display: inline-block;
  }

  svg {
    margin-right: 4px;
  }
`;
export const ErrorMessage = styled(Warning)`
  color: ${COLORS.critical_500};
`;

const StateColors = {
  [States.Default]: COLORS.neutral_500,
  [States.Valid]: COLORS.correct_500,
  [States.Invalid]: COLORS.critical_500,
}

export const ValidationMessage = styled(Warning)<{ type: States.Valid | States.Invalid | States.Default}>`
  color: ${(props) => StateColors[props.type]};
`;

export const NoteMessage = styled(Warning)`
  color: ${COLORS.neutral_500};
`;
export const AutofilledMessage = styled(Warning)`
  color: ${COLORS.neutral_600};
`;

export { ValidationStyling, InputFieldStyling };

export const StyledIcon = styled.div`
  border-radius: 50%;
`;

export const SearchIconWrapper = styled(StyledIcon)`
  position: absolute;
  border-radius: 50%;
`;
